import React, { useMemo, useState } from "react";
import { Loader, PageHeader } from "../../../components/ui";
import { strings } from "../../../strings/strings";
import { useAppQuery, useAppMutation } from "../../../api/fetchClient";
import { GET_VERSION_URL, UPDATE_VERSION_URL } from "../../../api/url";
import { QUERY_KEYS } from "../../../api/queryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import AddVersionModal from "./AddVersionModal";
import ConfirmUpdateModal from "./ConfirmUpdateModal";

const initialForm = {
  androidVersion: "",
  androidCode: "",
  androidLink: "",
  iosVersion: "",
  iosCode: "",
  iosLink: "",
};

const Settings = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [pendingData, setPendingData] = useState(null);
  const [originalData, setOriginalData] = useState(null);

  const queryClient = useQueryClient();
  const mutation = useAppMutation();

  const { data, isLoading, error } = useAppQuery({
    queryKey: QUERY_KEYS.APP_VERSION,
    url: GET_VERSION_URL,
    method: "GET",
  });

  const appVersion = data?.results?.version || null;

  const versionId = data?.results?.id || appVersion?.id || appVersion?.android?.id || appVersion?.ios?.id || null;

  const isEdit = useMemo(() => editingId !== null, [editingId]);

  const isValidUrl = (value) => {
    if (!trim(value)) return false;

    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";

    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const openEdit = () => {
    if (!appVersion) return;

    const formData = {
      androidVersion: appVersion?.android?.version_name || "",
      androidCode: appVersion?.android?.version_code || appVersion?.android?.code || "",
      androidLink: appVersion?.android?.store_url || "",
      iosVersion: appVersion?.ios?.version_name || "",
      iosCode: appVersion?.ios?.version_code || appVersion?.ios?.code || "",
      iosLink: appVersion?.ios?.store_url || "",
    };

    setEditingId(versionId || "edit");
    setErrors({});
    setForm(formData);
    setOriginalData(formData);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setErrors({});
    setEditingId(null);
    setOriginalData(null);
    setForm(initialForm);
  };

  const onChange = (key) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };
  const trim = (value) => {
    if (value == null) return "";
    return String(value).trim();
  };

  const validate = () => {
    const next = {};

    if (!trim(form.androidVersion)) {
      next.androidVersion = strings.androidVersionRequired;
    }

    if (!trim(form.androidCode)) {
      next.androidCode = strings.androidCodeRequired;
    }

    if (!trim(form.iosVersion)) {
      next.iosVersion = strings.iosVersionRequired;
    }

    if (!trim(form.iosCode)) {
      next.iosCode = strings.iosCodeRequired;
    }

    if (!isValidUrl(form.androidLink)) {
      next.androidLink = strings.androidLinkInvalid;
    }

    if (!isValidUrl(form.iosLink)) {
      next.iosLink = strings.iosLinkInvalid;
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;

    const payload = {};

    if (trim(form.androidVersion) !== trim(originalData?.androidVersion)) {
      payload.androidAppVersion = trim(form.androidVersion);
    }

    if (trim(form.androidCode) !== trim(originalData?.androidCode)) {
      payload.androidVersionCode = trim(form.androidCode);
    }

    if (trim(form.androidLink) !== trim(originalData?.androidLink)) {
      payload.androidLink = trim(form.androidLink);
    }

    if (trim(form.iosVersion) !== trim(originalData?.iosVersion)) {
      payload.iosAppVersion = trim(form.iosVersion);
    }

    if (trim(form.iosCode) !== trim(originalData?.iosCode)) {
      payload.iosVersionCode = trim(form.iosCode);
    }

    if (trim(form.iosLink) !== trim(originalData?.iosLink)) {
      payload.iosLink = trim(form.iosLink);
    }

    if (Object.keys(payload).length === 0) {
      toast.info(strings.noChanges);
      return;
    }

    setPendingData(payload);
    setConfirmModalOpen(true);
  };

  const handleConfirmUpdate = async () => {
    if (!pendingData) return;

    if (!versionId) {
      toast.error("Version ID not found. Cannot update.");
      return;
    }

    try {
      await mutation.mutateAsync({
        url: `${UPDATE_VERSION_URL}?id=${versionId}`,
        method: "PUT",
        data: pendingData,
      });

      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.APP_VERSION });
      toast.success(strings.versionUpdatedSuccess);

      setConfirmModalOpen(false);
      setModalOpen(false);
      setEditingId(null);
      setPendingData(null);
      setOriginalData(null);
      setForm(initialForm);
    } catch (err) {
      toast.error(err?.apiMessage || "Something went wrong");
    }
  };

  const handleCancelUpdate = () => {
    setConfirmModalOpen(false);
    setPendingData(null);
  };

  return (
    <div>
      <PageHeader title={strings.settingsTitle} description={strings.settingDescription} />

      {isLoading && (
        <div className="mt-6 rounded-xl border bg-white p-8 text-center">
          <Loader />
        </div>
      )}

      {!isLoading && !error && !appVersion && (
        <div className="mt-6 rounded-xl border bg-white p-8 text-center">
          <p className="text-sm text-gray-600">{strings.noAppVersionAdded}</p>
        </div>
      )}

      {!isLoading && !error && appVersion && (
        <div className="mt-6 rounded-xl border bg-white p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-secondary">{strings.appVersion}</h3>
            </div>

            <div className="flex gap-2">
              <button onClick={openEdit} className="px-3 py-2 text-sm rounded-lg border hover:bg-gray-50">
                {strings.edit}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="space-y-4 border border-secondary/15 rounded-lg p-3">
              <span className="pb-3 text-secondary font-semibold">{strings.android}</span>
              <div className="rounded-lg border bg-gray-50 p-3">
                <p className="text-xs font-bold text-secondary">{strings.androidAppVersion}</p>
                <p className="text-sm font-medium text-secondary mt-1">{appVersion?.android?.version_name || "-"}</p>
              </div>

              <div className="rounded-lg border bg-gray-50 p-3">
                <p className="text-xs font-bold text-secondary">{strings.androidAppCode || "Android App Code"}</p>
                <p className="text-sm font-medium text-secondary mt-1">
                  {appVersion?.android?.version_code || appVersion?.android?.code || "-"}
                </p>
              </div>

              <div className="rounded-lg border bg-gray-50 p-3">
                <p className="text-xs font-bold text-secondary">{strings.androidLink}</p>

                {appVersion?.android?.store_url ? (
                  <a
                    href={appVersion?.android?.store_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-blue-600 hover:underline break-all mt-1 inline-block"
                  >
                    {appVersion?.android?.store_url}
                  </a>
                ) : (
                  <p className="text-sm font-medium text-gray-900 mt-1">-</p>
                )}
              </div>

              <div className="rounded-lg border bg-gray-50 p-3">
                <p className="text-xs font-bold text-secondary">{strings.lastUpdatedAt}</p>
                <p className="text-sm font-medium text-secondary mt-1">{formatDate(appVersion?.android?.updated_at)}</p>
              </div>
            </div>

            <div className="space-y-4 border border-secondary/15 rounded-lg p-3">
              <span className="pb-3 text-secondary font-semibold">{strings.ios}</span>
              <div className="rounded-lg border bg-gray-50 p-3">
                <p className="text-xs font-bold text-secondary">{strings.iosAppVersion}</p>
                <p className="text-sm font-medium text-secondary mt-1">{appVersion?.ios?.version_name || "-"}</p>
              </div>

              <div className="rounded-lg border bg-gray-50 p-3">
                <p className="text-xs font-bold text-secondary">{strings.iosAppCode || "iOS App Code"}</p>
                <p className="text-sm font-medium text-secondary mt-1">
                  {appVersion?.ios?.version_code || appVersion?.ios?.code || "-"}
                </p>
              </div>

              <div className="rounded-lg border bg-gray-50 p-3">
                <p className="text-xs font-bold text-secondary">{strings.iosLink}</p>

                {appVersion?.ios?.store_url ? (
                  <a
                    href={appVersion?.ios?.store_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-blue-600 hover:underline break-all mt-1 inline-block"
                  >
                    {appVersion?.ios?.store_url}
                  </a>
                ) : (
                  <p className="text-sm font-medium text-gray-900 mt-1">-</p>
                )}
              </div>

              <div className="rounded-lg border bg-gray-50 p-3">
                <p className="text-xs font-bold text-secondary">{strings.lastUpdatedAt}</p>
                <p className="text-sm font-medium text-secondary mt-1">{formatDate(appVersion?.ios?.updated_at)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <AddVersionModal
        open={modalOpen}
        onClose={closeModal}
        isEdit={isEdit}
        form={form}
        errors={errors}
        onChange={onChange}
        onSubmit={onSubmit}
        isPending={mutation.isPending}
      />

      <ConfirmUpdateModal
        open={confirmModalOpen}
        onCancel={handleCancelUpdate}
        onConfirm={handleConfirmUpdate}
        isPending={mutation.isPending}
      />
    </div>
  );
};

export default Settings;
